import { lazy, Suspense } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useAuthContext } from '@/app/providers/AuthProvider';
import { HouseholdProvider, useHouseholdContext } from '@/app/providers/HouseholdProvider';
import { HouseholdDataProvider, useHouseholdData } from '@/app/providers/HouseholdDataProvider';
import { SettingsProvider, useSettingsContext } from '@/app/providers/SettingsProvider';
import { CategoriesProvider } from '@/app/providers/CategoriesProvider';
import { PeriodProvider } from '@/app/providers/PeriodProvider';
import { ModalsProvider } from '@/app/providers/ModalsProvider';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import { LoginPage, HouseholdSelector } from '@/features/auth';
import { OnboardingWizard } from '@/features/onboarding';
import { router } from './Router';

const PreviewPage = lazy(() => import('@/shared/pages/PreviewPage'));

const LoadingScreen = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a' }}>
    <p style={{ color: '#64748b' }}>{label}</p>
  </div>
);

function HouseholdGate({ children }) {
  const { user, handleLogout } = useAuthContext();
  const household = useHouseholdContext();
  const { householdId, householdLoading } = household;

  if (householdLoading) return <LoadingScreen label="Loading household..." />;

  if (!householdId) {
    return (
      <HouseholdSelector
        user={user}
        householdMode={household.householdMode}
        setHouseholdMode={household.setHouseholdMode}
        householdError={household.householdError}
        newHouseholdName={household.newHouseholdName}
        setNewHouseholdName={household.setNewHouseholdName}
        joinCode={household.joinCode}
        setJoinCode={household.setJoinCode}
        setupLoading={household.setupLoading}
        onCreateHousehold={household.handleCreateHousehold}
        onJoinHousehold={household.handleJoinHousehold}
        onLogout={handleLogout}
      />
    );
  }

  return children;
}

function OnboardingGate({ children }) {
  const { data, loading } = useHouseholdData();
  const { t, budgetMode, updateSettings, settings } = useSettingsContext();

  if (loading || !data) return children; // skeleton handled inside AppLayout
  const { txns = [], budgets = {} } = data;
  const showOnboarding = txns.length === 0 && Object.keys(budgets).length === 0 && !settings.onboardingComplete;
  if (showOnboarding) {
    return <OnboardingWizard theme={t} updateSettings={updateSettings} budgetMode={budgetMode} />;
  }
  return children;
}

export default function App() {
  const { user, authLoading, authError, loginLoading, handleLogin } = useAuthContext();

  if (typeof window !== 'undefined' && window.location.pathname === '/preview') {
    return (
      <Suspense fallback={<LoadingScreen label="Loading..." />}>
        <PreviewPage />
      </Suspense>
    );
  }

  if (authLoading) return <LoadingScreen label="Loading..." />;
  if (!user) return <LoginPage onLogin={handleLogin} error={authError} loading={loginLoading} />;

  return (
    <ErrorBoundary name="the app">
      <HouseholdProvider>
        <HouseholdGate>
          <HouseholdDataProvider>
            <SettingsProvider>
              <CategoriesProvider>
                <PeriodProvider>
                  <ModalsProvider>
                    <OnboardingGate>
                      <RouterProvider router={router} />
                    </OnboardingGate>
                  </ModalsProvider>
                </PeriodProvider>
              </CategoriesProvider>
            </SettingsProvider>
          </HouseholdDataProvider>
        </HouseholdGate>
      </HouseholdProvider>
    </ErrorBoundary>
  );
}
